import { useMe } from "api-hooks/use-me"
import { usePreferredWorkspaceStore } from "core/hooks/use-preferred-workspace-store"
import {
  resolvePreferredWorkspaceId,
  type PreferredWorkspaceSource,
} from "core/lib/resolve-preferred-workspace-id"

export interface ResolvedPreferredWorkspace {
  preferredWorkspaceId: string | null
  source: PreferredWorkspaceSource
  serverPreferredWorkspaceId: string | null
  localPreferredWorkspaceId: string | null
  /**
   * Whether the server value is still loading. While true, `preferredWorkspaceId`
   * is the localStorage fallback, which `source` reports as `local_fallback` even
   * once the server turns out to hold a different value. Callers that persist the
   * resolved id must wait for this, or they write the fallback over the server
   * preference.
   */
  isServerPreferencePending: boolean
}

/**
 * Single read path for the preferred workspace id: the server value from the
 * session, falling back to the persisted localStorage value.
 */
export function useResolvedPreferredWorkspaceId(): ResolvedPreferredWorkspace {
  const { data: user, isPending: isServerPreferencePending } = useMe()
  const localPreferredWorkspaceId = usePreferredWorkspaceStore(
    (state) => state.preferredWorkspaceId
  )

  const serverPreferredWorkspaceId: string | null | undefined =
    user?.preferred_workspace_id

  const { preferredWorkspaceId, source } = resolvePreferredWorkspaceId({
    serverPreferredWorkspaceId,
    localPreferredWorkspaceId,
  })

  return {
    preferredWorkspaceId,
    source,
    serverPreferredWorkspaceId: serverPreferredWorkspaceId ?? null,
    localPreferredWorkspaceId,
    isServerPreferencePending,
  }
}
